import type { AccessMe, AccessScope, Permission } from '~/types/v2/Access'

type PermissionDecision =
  | { type: 'continue' }
  | { type: 'redirect'; path: string }

interface PermissionDecisionInput {
  /** The permission the page declares with `definePageMeta({ permission })`. */
  permission?: Permission
  /** The person's packed rules; undefined when they could not be loaded. */
  rules?: AccessMe['rules']
  scope: AccessScope
}

/**
 * Decide whether a page that declares a permission opens. Pure: the
 * middleware (~/middleware/07.permission.global.ts) loads the rules and
 * resolves the scope, then calls this.
 *
 * - No permission declared → continue; this rule has nothing to say.
 * - Rules not loaded → 403. Fails closed: these pages were Superadmin-only
 *   until permissions opened them, and the old API routes they call do not
 *   check permissions themselves.
 * - Otherwise the rules decide, against the page's organisation (and
 *   workspace, for a workspace permission).
 */
export function decidePermissionAction(
  input: PermissionDecisionInput,
): PermissionDecision {
  if (!input.permission) return { type: 'continue' }

  if (!input.rules) return { type: 'redirect', path: '/403' }

  const ability = createAccessAbility(input.rules)
  return canAccess(ability, input.permission, input.scope)
    ? { type: 'continue' }
    : { type: 'redirect', path: '/403' }
}

/**
 * The scope a page's permission is checked in: the route's organisation, by
 * id, from the person's own organisations (05.access has already required
 * membership), and its workspace. An organisation the person isn't in stays
 * undefined, which answers no.
 */
export function permissionScopeFor(
  params: { organizationSlug?: unknown; workspaceId?: unknown },
  organizations: { id: string; slug: string }[],
): AccessScope {
  const organizationSlug =
    typeof params.organizationSlug === 'string'
      ? params.organizationSlug
      : undefined
  return {
    organizationId: organizations.find(
      (organization) => organization.slug === organizationSlug,
    )?.id,
    workspaceId:
      typeof params.workspaceId === 'string' ? params.workspaceId : undefined,
  }
}

interface ResolvePermissionInput {
  permission?: Permission
  scope: AccessScope
  /** The rules as cached; undefined when they can't be loaded. */
  loadRules: () => Promise<AccessMe['rules'] | undefined>
  /** The rules fetched afresh; undefined when the fetch fails. */
  refreshRules: () => Promise<AccessMe['rules'] | undefined>
}

/**
 * The page rule's whole flow, for the middleware: decide on the cached rules,
 * and before refusing fetch them once more and decide again. Rules can lag a
 * role change by up to the two-minute refresh, so a role granted moments ago
 * still opens the page.
 */
export async function resolvePermissionAction(
  input: ResolvePermissionInput,
): Promise<PermissionDecision> {
  if (!input.permission) return { type: 'continue' }

  const decision = decidePermissionAction({
    permission: input.permission,
    rules: await input.loadRules(),
    scope: input.scope,
  })
  if (decision.type === 'continue') return decision

  const freshRules = await input.refreshRules()
  if (!freshRules) return decision
  return decidePermissionAction({
    permission: input.permission,
    rules: freshRules,
    scope: input.scope,
  })
}
